import {gql, useMutation, useQuery} from '@apollo/client';
import {useLocation} from 'react-router-dom';
import {useAccountContext} from '../../contexts/AccountContext';
import {UPDATE_REVIEW} from '../../hooks/useUpdatePrecursor';
import {CreateReview, CreateReviewVariables} from '../../schema-types/CreateReview';
import {GetReviews, GetReviewsVariables} from '../../schema-types/GetReviews';
import {UpdateReview, UpdateReviewVariables} from '../../schema-types/UpdateReview';
import validateUUID from '../../utils/validateUUID';
import {
  CREATE_REVIEW,
  GET_ACCOUNT_BASIC,
  GET_ACCOUNT_REVIEWS,
} from '../AccountOverview/accountOverviewQueries';
import {getAccountIdFromPath} from '../Admin/AdminDialogHelper';
import {toUUIDOrNull} from '../../graphql/uuid';

const GET_PREPRECURSOR_REVIEWS_QUERY = gql`
  query GetReviews($accountId: UUID!) {
    accountDetails(accountId: $accountId) {
      id
      name
      reviews {
        id
      }
      draftReviews {
        id
        account {
          id
          name
        }
        reviewReason
        name
        dateBooked
        dateScheduled
      }
    }
  }
`;

export const usePrePrecursorGQL = () => {
  const {accountId: accountIdFromContext} = useAccountContext();
  const {pathname} = useLocation();
  const accountIdFromPath = getAccountIdFromPath(pathname);
  const accountId = accountIdFromContext ?? accountIdFromPath;
  const {data: accountReviewsData, refetch: refetchAccountReviews} = useQuery<
    GetReviews,
    GetReviewsVariables
  >(GET_PREPRECURSOR_REVIEWS_QUERY, {
    variables: {
      accountId: toUUIDOrNull(accountId)!,
    },
    skip: !validateUUID(accountId),
  });

  const [createReview, {loading: createReviewLoading}] = useMutation<
    CreateReview,
    CreateReviewVariables
  >(CREATE_REVIEW, {
    awaitRefetchQueries: false,
    refetchQueries: [
      {
        query: GET_ACCOUNT_BASIC,
        variables: {
          accountId: toUUIDOrNull(accountId)!,
          idType: 'id',
        },
      },
      {
        query: GET_PREPRECURSOR_REVIEWS_QUERY,
        variables: {
          accountId: toUUIDOrNull(accountId)!,
        },
      },
      {
        query: GET_ACCOUNT_REVIEWS,
        variables: {
          accountId: toUUIDOrNull(accountId)!,
          idType: 'id',
          curoSync: true,
        },
      },
    ],
  });

  const [updateReview, {loading: updateReviewLoading}] = useMutation<
    UpdateReview,
    UpdateReviewVariables
  >(UPDATE_REVIEW, {
    refetchQueries: [
      {
        query: GET_ACCOUNT_BASIC,
        variables: {
          accountId: toUUIDOrNull(accountId)!,
          idType: 'id',
        },
      },
      {
        query: GET_ACCOUNT_REVIEWS,
        variables: {
          accountId: toUUIDOrNull(accountId)!,
          idType: 'id',
        },
      },
    ],
  });

  return {
    accountReviewsData,
    refetchAccountReviews,
    createReview,
    updateReview,
    createReviewLoading,
    updateReviewLoading,
  };
};
